/**
 * Bridges the UI to the durable outbox.
 *
 * Every mutation queues first and updates the screen optimistically; the
 * drainer sends when it can. Nothing is fire-and-forget.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import type { SimklClient } from "@/api/client";
import { log } from "@/lib/log";
import { enqueue, OutboxDrainer, pending } from "@/store/outbox";
import type { OutboxAction, OutboxEntry } from "@/store/outbox";

export interface OutboxState {
  queued: readonly OutboxEntry[];
  submit: (action: OutboxAction, onSent?: () => void) => void;
}

/**
 * How long a write waits before it is sent, so an immediate correction can
 * cancel it in the queue rather than chase it over the network.
 */
const COALESCE_MS = 250;

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** What an action did, without the episode list, which is only ever noise. */
function summarise(action: OutboxAction): Record<string, unknown> {
  if (action.kind === "watch-many" || action.kind === "unwatch-many") {
    const first = action.targets[0];
    return {
      kind: action.kind,
      show: first?.simklId,
      episodes: action.targets.length,
      seasons: [...new Set(action.targets.map((target) => target.season))],
    };
  }
  if (action.kind === "watch" || action.kind === "unwatch") {
    return { kind: action.kind, show: action.target.simklId, at: action.target };
  }
  return { kind: action.kind, show: action.simklId };
}

export function useOutbox(client: SimklClient, enabled: boolean): OutboxState {
  const drainer = useMemo(() => new OutboxDrainer(client), [client]);
  const [queued, setQueued] = useState<readonly OutboxEntry[]>([]);

  const sync = useCallback(() => {
    void drainer.drain().then(() => pending().then(setQueued));
  }, [drainer]);

  /**
   * Queues, then drains, then tells the caller. The refetch used to run on a
   * fixed 1200ms timer, which was both slower than the write (183ms at Simkl)
   * and capable of firing before it. Waiting for the drain is shorter and
   * correct.
   */
  const submit = useCallback(
    (action: OutboxAction, onSent?: () => void) => {
      const started = performance.now();
      log("submit", summarise(action));
      void enqueue(action)
        .then((entry) => {
          if (entry === null) log("cancelled by its inverse", summarise(action));
          return pending();
        })
        .then(setQueued)
        // A short pause before sending, so a correction made in the next
        // moment cancels the thing it corrects instead of racing it. The
        // screen already moved, so this costs nothing anyone can perceive.
        .then(() => pause(COALESCE_MS))
        .then(() => drainer.drain())
        .then((result) => {
          log("drained", { ...result, ms: Math.round(performance.now() - started) });
        })
        .then(() => pending())
        .then((entries) => {
          setQueued(entries);
          if (entries.length > 0) {
            log(
              "still queued",
              entries.map((entry) => ({
                kind: entry.action.kind,
                attempts: entry.attempts,
                error: entry.lastError,
              })),
            );
          }
        })
        .then(() => onSent?.());
    },
    [drainer],
  );

  // Drain on start and whenever the network comes back.
  useEffect(() => {
    if (!enabled) return;
    sync();
    window.addEventListener("online", sync);
    return () => {
      window.removeEventListener("online", sync);
    };
  }, [enabled, sync]);

  return { queued, submit };
}
